import React, { useState } from 'react'
import { HiPlusCircle, HiKey, HiSparkles, HiCamera } from 'react-icons/hi'
import { useNavigate } from 'react-router-dom'
import { api, ApiError, getDeviceId } from '../utils/api'
import logger from '../utils/logger'
import MenuLoadingModal from './MenuLoadingModal'
import { trackPollCreated, trackMenuImageUploaded, trackPollJoined, trackErrorPollCreate, trackErrorMenuUpload } from '../utils/analytics'
import usePageTitle from '../hooks/usePageTitle'
import './HomePage.css'

function HomePage() {
  usePageTitle('Home')
  const navigate = useNavigate()
  const [title, setTitle] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [menuFile, setMenuFile] = useState(null)
  const [menuUploading, setMenuUploading] = useState(false)
  const [menuError, setMenuError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!title.trim()) {
      setError('Please enter a poll title')
      return
    }

    if (title.length > 150) {
      setError('Poll title must be 150 characters or less')
      return
    }

    setLoading(true)
    setError('')

    try {
      logger.info('Creating new poll', { title: title.substring(0, 50) + '...' })

      const deviceId = getDeviceId()
      const response = await api.post('/api/polls', { title: title.trim(), device_id: deviceId })

      logger.info('Poll created successfully', { pollId: response.id, shortCode: response.short_code })
      trackPollCreated(response.id, title.trim().length, 'manual')
      // Use short_code for navigation instead of UUID
      navigate(`/poll/${response.short_code}`)

    } catch (err) {
      const errorMessage = err instanceof ApiError
        ? err.message
        : 'Failed to create poll. Please try again.'

      logger.error('Failed to create poll', { error: errorMessage })
      trackErrorPollCreate(errorMessage)
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleMenuUpload = async (e) => {
    e.preventDefault()

    if (!menuFile) {
      setMenuError('Please select a menu image')
      return
    }

    setMenuUploading(true)
    setMenuError('')

    try {
      logger.info('Uploading menu image for poll creation', { fileName: menuFile.name, fileSize: menuFile.size })

      const formData = new FormData()
      formData.append('menu_image', menuFile)
      // Include device_id so the poll creator can be tracked for ownership
      formData.append('device_id', getDeviceId())

      // Read LLM provider preference from localStorage (set in Admin page)
      const llmProvider = localStorage.getItem('admin_llm_provider') || 'gemini'
      const headers = {
        'x-llm-provider': llmProvider
      }

      logger.info('Using LLM provider for menu analysis', { provider: llmProvider })

      const response = await api.postForm('/api/menu-polls', formData, headers)

      logger.info('Menu poll created successfully', { pollId: response.id, shortCode: response.short_code })
      trackMenuImageUploaded(menuFile.size, menuFile.type)
      trackPollCreated(response.id, 0, 'menu_ai')
      navigate(`/review/${response.short_code}`)

    } catch (err) {
      const errorMessage = err instanceof ApiError
        ? err.message
        : 'Failed to analyze menu image. Please try again.'

      logger.error('Failed to create poll from menu', { error: errorMessage })
      trackErrorMenuUpload(errorMessage, menuFile?.size)
      setMenuError(errorMessage)
    } finally {
      setMenuUploading(false)
    }
  }

  return (
    <div className="homepage-container fade-in">
      <div className="home-grid">
        <div className="card poll-card fade-in-up" style={{ animationDelay: '0.1s' }}>
          <div className="poll-card-header">
            <HiSparkles className="poll-icon-primary" />
            <h2 style={{ margin: 0 }}>Create a New Poll</h2>
          </div>
          <p className="poll-card-description">
            Start a realtime poll that others can join and vote on.
          </p>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="poll-title" className="form-label form-label-flex">
                <span>Poll Title</span>
              </label>
              <input
                id="poll-title"
                type="text"
                className="form-input"
                placeholder="What's your question?"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={loading}
                maxLength={150}
              />
              {error && (
                <div className="form-error slide-in">
                  {error}
                </div>
              )}
            </div>

            <button
              type="submit"
              className={`btn btn-primary full-width ${loading ? 'btn-loading' : ''}`}
              disabled={loading || !title.trim()}
            >
              <HiPlusCircle style={{ fontSize: '1.25rem' }} />
              {loading ? 'Creating Poll...' : 'Create Poll'}
            </button>
          </form>
        </div>

        <div className="card poll-card fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="poll-card-header">
            <HiCamera className="poll-icon-primary" />
            <h2 style={{ margin: 0 }}>Create Poll from Menu</h2>
          </div>
          <p className="poll-card-description">
            Upload a restaurant menu image and we'll automatically create a poll with all the dishes.
          </p>

          <form onSubmit={handleMenuUpload}>
            <div className="form-group">
              <label htmlFor="menu-image" className="form-label">
                Menu Image
              </label>
              <div className="file-input-wrapper">
                <input
                  id="menu-image"
                  type="file"
                  accept="image/*"
                  className="form-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    setMenuFile(file || null)
                    setMenuError('')
                  }}
                  disabled={menuUploading}
                  style={{ padding: '0.5rem' }}
                />
              </div>
              <small className="file-input-help">
                Supported formats: JPEG, PNG, WebP (max 10MB)
              </small>
              {menuError && (
                <div className="form-error slide-in">
                  {menuError}
                </div>
              )}
            </div>

            <button
              type="submit"
              className={`btn btn-primary full-width ${menuUploading ? 'btn-loading' : ''}`}
              disabled={menuUploading || !menuFile}
            >
              <HiCamera style={{ fontSize: '1.25rem' }} />
              {menuUploading ? 'Analyzing Menu...' : 'Generate Poll from Menu'}
            </button>
          </form>
        </div>

        <div className="card poll-card fade-in-up" style={{ animationDelay: '0.25s' }}>
          <div className="poll-card-header">
            <HiKey className="poll-icon-secondary" />
            <h3 style={{ margin: 0 }}>Join Existing Poll</h3>
          </div>
          <p className="poll-card-description" style={{ marginBottom: '1rem' }}>
            Have a poll code? Enter the 6-character code below to join.
          </p>
          <form onSubmit={(e) => {
            e.preventDefault()
            const pollCode = e.target.pollId.value.trim()
            if (pollCode) {
              logger.info('Joining existing poll', { pollCode })
              trackPollJoined(pollCode)
              navigate(`/poll/${pollCode}`)
            }
          }}>
            <div className="form-group">
              <label htmlFor="poll-id" className="form-label">
                Poll Code
              </label>
              <input
                id="poll-id"
                name="pollId"
                type="text"
                className="form-input poll-code-input"
                placeholder="Enter 6-character code..."
                maxLength={6}
              />
            </div>
            <button
              type="submit"
              className="btn btn-secondary full-width"
            >
              <HiKey />
              Join Poll
            </button>
          </form>
        </div>
      </div>

      <MenuLoadingModal show={menuUploading} />
    </div>
  )
}

export default HomePage
